import { useState, type MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useIntegrationsList, useIntegration } from '../hooks'
import { Card, Button, Badge, Table, PageLoading, ErrorMessage } from '../components'
import { integrationService } from '../api'
import { useAuthStore, apiFetch, API_BASE } from '../store/auth'
import type { BankTransaction, BankAccount } from '../api'

interface IntegrationCardProps {
  provider: string
  name: string
  description: string
  icon: string
  color: string
}

const PROVIDER_CONFIG: Record<string, Omit<IntegrationCardProps, 'provider'>> = {
  sumup: {
    name: 'SumUp',
    description: 'Yhdistä SumUp-maksupäätteeseen tapahtumien tuontiin',
    icon: '💳',
    color: '#1C2B4A',
  },
  op: {
    name: 'OP Pankki',
    description: 'Yhdistä OP-tiliisi tilitapahtumien tuontiin',
    icon: '🏦',
    color: '#0066B3',
  },
  holvi: {
    name: 'Holvi',
    description: 'Yhdistä Holvi-tiliisi tilitapahtumien tuontiin',
    icon: '🏦',
    color: '#E4002B',
  },
  nordea: {
    name: 'Nordea',
    description: 'Yhdistä Nordea-tiliisi tilitapahtumien tuontiin',
    icon: '🏦',
    color: '#0000A0',
  },
  danske: {
    name: 'Danske Bank',
    description: 'Yhdistä Danske Bank -tiliisi tilitapahtumien tuontiin',
    icon: '🏦',
    color: '#003B7E',
  },
  spankki: {
    name: 'S-Pankki',
    description: 'Yhdistä S-Pankki-tiliisi tilitapahtumien tuontiin',
    icon: '🏦',
    color: '#F26522',
  },
  alandsbanken: {
    name: 'Ålandsbanken',
    description: 'Yhdistä Ålandsbanken-tiliisi tilitapahtumien tuontiin',
    icon: '🏦',
    color: '#005A8C',
  },
  aktia: {
    name: 'Aktia',
    description: 'Yhdistä Aktia-tiliisi tilitapahtumien tuontiin',
    icon: '🏦',
    color: '#8B1A4A',
  },
}

const ENABLE_BANKING_PROVIDERS = new Set(['holvi', 'op', 'nordea', 'danske', 'spankki', 'alandsbanken', 'aktia'])

function IntegrationDetail({ provider, onBack }: { provider: string; onBack: () => void }) {
  const { t } = useTranslation()
  const {
    status,
    statusLoading,
    statusError,
    accounts,
    selectedAccountId,
    setSelectedAccountId,
    connect,
    isConnecting,
    disconnect,
    isDisconnecting,
  } = useIntegration(provider)

  const { activeOrg } = useAuthStore()
  const config = PROVIDER_CONFIG[provider]

  // Import state — paginated for progress feedback
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0])
  const [endDateInvalid, setEndDateInvalid] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  const [importError, setImportError] = useState<string | null>(null)
  const [importedTx, setImportedTx] = useState<BankTransaction[]>([])
  const [importedCount, setImportedCount] = useState<number | null>(null)
  const [duplicateCount, setDuplicateCount] = useState<number>(0)
  const [errorCount, setErrorCount] = useState<number>(0)
  const [fetchedCount, setFetchedCount] = useState<number>(0)
  const [importProgress, setImportProgress] = useState<string | null>(null)

  // SumUp card sales import state
  const [salesStartDate, setSalesStartDate] = useState('')
  const [salesEndDate, setSalesEndDate] = useState(new Date().toISOString().split('T')[0])
  const [isImportingSales, setIsImportingSales] = useState(false)
  const [salesImportError, setSalesImportError] = useState<string | null>(null)
  const [salesImportedCount, setSalesImportedCount] = useState<number | null>(null)
  const [salesDuplicateCount, setSalesDuplicateCount] = useState<number>(0)
  const [salesFetchedCount, setSalesFetchedCount] = useState<number>(0)
  const [salesImportProgress, setSalesImportProgress] = useState<string | null>(null)

  // Auto-sync state
  const [syncing, setSyncing] = useState(false)
  const [syncResult, setSyncResult] = useState<string | null>(null)

  const handleImport = async () => {
    if (!activeOrg?.id) return
    if (endDateInvalid) {
      setImportError('Loppupäivämäärä on virheellinen (esim. helmikuussa ei ole 31. päivää)')
      return
    }
    setIsImporting(true)
    setImportError(null)
    setImportedCount(null)
    setDuplicateCount(0)
    setErrorCount(0)
    setFetchedCount(0)
    setImportedTx([])
    setImportProgress(t('integrations.fetchingTransactions'))

    let cursor: string | undefined
    let totalImported = 0
    let totalDuplicates = 0
    let totalErrors = 0
    let totalFetched = 0
    const allTx: BankTransaction[] = []
    let firstError: string | undefined
    let pageLimitReached = false
    const MAX_PAGES = 200  // safety cap — 200 × 100 = 20 000 transactions

    try {
      // Loop through pages until no more results or safety cap reached
      for (let page = 0; page < MAX_PAGES; page++) {
        const result = await integrationService.fetchAndImportPage(
          provider,
          activeOrg.id,
          {
            startDate: startDate || undefined,
            endDate: endDate || new Date().toISOString().split('T')[0],
            accountId: selectedAccountId || undefined,
            cursor,
          }
        )

        totalFetched += result.transactions.length
        totalImported += result.imported
        totalDuplicates += result.duplicates
        totalErrors += result.errors
        if (result.firstError && !firstError) firstError = result.firstError
        allTx.push(...result.transactions)

        // Update running progress
        setFetchedCount(totalFetched)
        setImportProgress(`Haettu ${totalFetched} tapahtumaa — tuotu ${totalImported} uutta…`)

        if (!result.hasMore) break
        const nextCursor = result.nextCursor ?? undefined
        // Extra safety: if cursor didn't change the backend detected a stall (shouldn't happen,
        // but guard here too so the loop always terminates)
        if (nextCursor === cursor) break
        cursor = nextCursor
        if (page === MAX_PAGES - 1) pageLimitReached = true
      }

      setImportedTx(allTx)
      setImportedCount(totalImported)
      setDuplicateCount(totalDuplicates)
      setErrorCount(totalErrors)
      setImportProgress(null)
      if (pageLimitReached) {
        setImportError(`Sivuraja (${MAX_PAGES} sivua / ${totalFetched} tapahtumaa) saavutettiin — tarkenna aikaväliä hakeaksesi lisää`)
      } else if (firstError) {
        setImportError(`Virhe tallennuksessa: ${firstError}`)
      }
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Tapahtumien tuonti epäonnistui')
      setImportProgress(null)
      // Keep partial counts visible
      setImportedTx(allTx)
      setImportedCount(totalImported)
      setDuplicateCount(totalDuplicates)
      setErrorCount(totalErrors)
    } finally {
      setIsImporting(false)
    }
  }

  const handleImportSales = async () => {
    if (!activeOrg?.id) return
    setIsImportingSales(true)
    setSalesImportError(null)
    setSalesImportedCount(null)
    setSalesDuplicateCount(0)
    setSalesFetchedCount(0)
    setSalesImportProgress(t('integrations.fetchingCardPurchases'))

    let cursor: string | undefined
    let totalImported = 0
    let totalDuplicates = 0
    let totalFetched = 0
    let pageLimitReached = false
    const MAX_PAGES = 200

    try {
      for (let page = 0; page < MAX_PAGES; page++) {
        const params = new URLSearchParams({
          organizationId: activeOrg.id,
          import: 'true',
          ...(salesStartDate && { startDate: salesStartDate }),
          ...(salesEndDate && { endDate: salesEndDate }),
          ...(cursor && { cursor }),
        })
        const resp = await apiFetch(`${API_BASE}/integrations/sumup/sales?${params}`)
        if (!resp.ok) {
          let msg = `HTTP ${resp.status}`
          try {
            const body = await resp.json() as { error?: string }
            if (body.error) msg = body.error
          } catch { /* ignore */ }
          throw new Error(msg)
        }
        const data = await resp.json() as {
          transactions: { id: string }[]
          imported: number
          duplicates: number
          has_more: boolean
          next_cursor: string | null
        }

        totalFetched += data.transactions.length
        totalImported += data.imported
        totalDuplicates += data.duplicates ?? 0

        setSalesFetchedCount(totalFetched)
        setSalesImportProgress(`Haettu ${totalFetched} korttioston — tuotu ${totalImported} uutta…`)

        if (!data.has_more) break
        const nextCursor = data.next_cursor ?? undefined
        if (!nextCursor || nextCursor === cursor) break
        cursor = nextCursor
        if (page === MAX_PAGES - 1) pageLimitReached = true
      }

      setSalesImportedCount(totalImported)
      setSalesDuplicateCount(totalDuplicates)
      setSalesImportProgress(null)
      if (pageLimitReached) {
        setSalesImportError(`Sivuraja saavutettu (${totalFetched} tapahtumaa) — tarkenna aikaväliä`)
      }
    } catch (err) {
      setSalesImportError(err instanceof Error ? err.message : 'Korttiostoja ei voitu tuoda')
      setSalesImportProgress(null)
      setSalesImportedCount(totalImported)
      setSalesDuplicateCount(totalDuplicates)
    } finally {
      setIsImportingSales(false)
    }
  }

  const handleManualSync = async () => {
    if (!activeOrg?.id) return
    setSyncing(true)
    setSyncResult(null)
    try {
      const response = await apiFetch(`${API_BASE}/bank-sync/trigger`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ organization_id: activeOrg.id, bank_type: provider }),
      })
      const data = await response.json() as { success: boolean; transactions_imported?: number; error?: string }
      if (data.success) {
        setSyncResult(`Synkronoitu — tuotu ${data.transactions_imported ?? 0} uutta tapahtumaa`)
      } else {
        setSyncResult(`Virhe: ${data.error ?? 'Tuntematon virhe'}`)
      }
    } catch {
      setSyncResult('Synkronointi epäonnistui')
    } finally {
      setSyncing(false)
    }
  }

  if (statusLoading) {
    return (
      <div className="p-8">
        <button onClick={onBack} className="mb-4 text-indigo-600 hover:text-indigo-800">
          ← Takaisin integraatioihin
        </button>
        <PageLoading />
      </div>
    )
  }

  const txColumns = [
    {
      key: 'date',
      header: 'Päivämäärä',
      render: (tx: BankTransaction) => new Date(tx.date).toLocaleDateString('fi-FI'),
    },
    {
      key: 'description',
      header: 'Kuvaus',
      render: (tx: BankTransaction) => (
        <span className="max-w-xs truncate block">{tx.description || '-'}</span>
      ),
    },
    {
      key: 'amount',
      header: 'Summa',
      render: (tx: BankTransaction) => `${tx.amount.toFixed(2)} EUR`,
    },
  ]

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <button onClick={onBack} className="mb-4 text-indigo-600 hover:text-indigo-800">
        ← Takaisin integraatioihin
      </button>

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">
          {config.icon} {config.name}
        </h1>
        {status?.connected && (
          <Button variant="danger" onClick={disconnect} isLoading={isDisconnecting}>
            Katkaise yhteys
          </Button>
        )}
      </div>

      {statusError && <div className="mb-4"><ErrorMessage message={statusError} /></div>}

      {/* Connection Status */}
      <Card className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Yhteystila</h2>
            {status?.connected ? (
              <div className="mt-2">
                <Badge variant="success">Yhdistetty</Badge>
                {(status as any).merchant_name && (
                  <p className="mt-2 text-sm text-gray-600">Kauppias: {(status as any).merchant_name}</p>
                )}
                {(status as any).connected_at && (
                  <p className="mt-1 text-sm text-gray-500">
                    Yhdistetty: {new Date((status as any).connected_at).toLocaleDateString('fi-FI')}
                  </p>
                )}
              </div>
            ) : (
              <div className="mt-2">
                <Badge variant="default">Ei yhdistetty</Badge>
                <p className="mt-2 text-sm text-gray-600">{config.description}</p>
              </div>
            )}
          </div>
          {!status?.connected && (
            <div className="flex items-center gap-3">
              <Button
                onClick={connect}
                isLoading={isConnecting}
                style={{ backgroundColor: config.color }}
                className="text-white"
              >
                Yhdistä {config.name}
              </Button>
            </div>
          )}
        </div>
      </Card>

      {/* Account Selection for multi-account banks */}
      {status?.connected && ENABLE_BANKING_PROVIDERS.has(provider) && accounts.length > 0 && (
        <Card className="mb-6" title={t('integrations.selectAccount')}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {accounts.map((acc: BankAccount) => (
              <div
                key={acc.id}
                className={`p-4 rounded-lg border cursor-pointer transition ${
                  selectedAccountId === acc.id ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setSelectedAccountId(acc.id)}
              >
                <p className="font-medium text-gray-900">{acc.name}</p>
                {acc.iban && <p className="text-sm text-gray-500">{acc.iban}</p>}
                <p className="mt-2 text-lg font-semibold text-gray-900">
                  {acc.balance.toFixed(2)} {acc.currency}
                </p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Manual import */}
      {status?.connected && (
        <Card
          className="mb-6"
          title="Tuo tapahtumat"
          action={
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-500">Alkaen</span>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </label>
              <span className="text-gray-500 mt-4">–</span>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-500">Päättyen</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDateInvalid(e.target.validity.badInput)
                    setEndDate(e.target.value)
                  }}
                  className={`px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 ${endDateInvalid ? 'border-red-400' : ''}`}
                />
              </label>
              <Button onClick={handleImport} isLoading={isImporting} className="mt-4">
                Tuo tapahtumat
              </Button>
            </div>
          }
        >
          {importProgress && (
            <div className="mb-3 p-3 rounded text-sm border bg-indigo-50 border-indigo-200 text-indigo-800 flex items-center gap-2">
              <span className="inline-block w-4 h-4 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
              {importProgress}
            </div>
          )}
          {importError && <div className="mb-3"><ErrorMessage message={importError} /></div>}
          {importedCount !== null && (
            <>
              {importedCount > 0 && (
                <div className="mb-3 p-3 rounded text-sm border bg-green-50 border-green-200 text-green-800">
                  Tuotu {importedCount} uutta tapahtumaa Kirstunvartijaan.
                  {' '}Haettiin yhteensä {fetchedCount} tapahtumaa pankista.
                </div>
              )}
              {importedCount === 0 && duplicateCount > 0 && errorCount === 0 && (
                <div className="mb-3 p-3 rounded text-sm border bg-blue-50 border-blue-200 text-blue-800">
                  Kaikki {duplicateCount} tapahtumaa on jo Kirstunvartijassa — ei uusia.
                </div>
              )}
              {errorCount > 0 && !importError && (
                <div className="mb-3 p-3 rounded text-sm border bg-red-50 border-red-200 text-red-800">
                  {errorCount} tapahtumaa epäonnistui tallennuksessa.
                  {importedCount > 0 && ` Tuotu ${importedCount} onnistuneesti.`}
                  {' '}Haettiin yhteensä {fetchedCount} tapahtumaa pankista.
                </div>
              )}
              {importedCount === 0 && duplicateCount === 0 && errorCount === 0 && !importError && (
                <div className="mb-3 p-3 rounded text-sm border bg-blue-50 border-blue-200 text-blue-800">
                  Ei tapahtumia haetulla aikavälillä.
                </div>
              )}
            </>
          )}
          {importedTx.length > 0 && (
            <Table
              columns={txColumns}
              data={importedTx}
              keyExtractor={(tx) => tx.id}
              emptyMessage=""
            />
          )}
          {importedCount === null && !isImporting && (
            <p className="text-sm text-gray-500">
              Valitse aikaväli ja paina "Tuo tapahtumat" — tapahtumat haetaan pankista ja tallennetaan suoraan Kirstunvartijaan.
            </p>
          )}
        </Card>
      )}

      {/* SumUp card sales import */}
      {status?.connected && provider === 'sumup' && (
        <Card
          className="mb-6"
          title="Tuo korttiostot"
          action={
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-500">Alkaen</span>
                <input
                  type="date"
                  value={salesStartDate}
                  onChange={(e) => setSalesStartDate(e.target.value)}
                  className="px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </label>
              <span className="text-gray-500 mt-4">–</span>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-500">Päättyen</span>
                <input
                  type="date"
                  value={salesEndDate}
                  onChange={(e) => setSalesEndDate(e.target.value)}
                  className="px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </label>
              <Button onClick={handleImportSales} isLoading={isImportingSales} className="mt-4">
                Tuo korttiostot
              </Button>
            </div>
          }
        >
          {salesImportProgress && (
            <div className="mb-3 p-3 rounded text-sm border bg-indigo-50 border-indigo-200 text-indigo-800 flex items-center gap-2">
              <span className="inline-block w-4 h-4 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
              {salesImportProgress}
            </div>
          )}
          {salesImportError && <div className="mb-3"><ErrorMessage message={salesImportError} /></div>}
          {salesImportedCount !== null && (
            <>
              {salesImportedCount > 0 && (
                <div className="mb-3 p-3 rounded text-sm border bg-green-50 border-green-200 text-green-800">
                  Tuotu {salesImportedCount} uutta korttioston myyntitapahtumaa.
                  {' '}Haettiin yhteensä {salesFetchedCount} tapahtumaa.
                </div>
              )}
              {salesImportedCount === 0 && salesDuplicateCount > 0 && (
                <div className="mb-3 p-3 rounded text-sm border bg-blue-50 border-blue-200 text-blue-800">
                  Kaikki {salesDuplicateCount} korttioston tapahtumaa on jo Kirstunvartijassa.
                </div>
              )}
              {salesImportedCount === 0 && salesDuplicateCount === 0 && !salesImportError && (
                <div className="mb-3 p-3 rounded text-sm border bg-blue-50 border-blue-200 text-blue-800">
                  Ei korttioston tapahtumia haetulla aikavälillä.
                </div>
              )}
            </>
          )}
          {salesImportedCount === null && !isImportingSales && (
            <p className="text-sm text-gray-500">
              Tuo SumUp-maksupäätteen korttiostot myyntitapahtumiksi. Korttiostot tallennetaan erilliseen myyntitauluun — ne eivät sekoitu pankin tilitapahtumiin.
            </p>
          )}
        </Card>
      )}

      {/* Auto-sync */}
      {status?.connected && (
        <Card title={t('integrations.syncNow')}>
          <p className="text-sm text-gray-600 mb-4">
            {t('integrations.syncDescription')}
          </p>
          <div className="flex items-center gap-3">
            <Button variant="secondary" onClick={handleManualSync} isLoading={syncing}>
              {t('integrations.syncNow')}
            </Button>
          </div>
          {syncResult && (
            <p className={`mt-3 text-sm ${syncResult.startsWith(t('common.error')) ? 'text-red-600' : 'text-green-700'}`}>
              {syncResult}
            </p>
          )}
          <p className="mt-3 text-xs text-gray-400">
            {t('integrations.autoScheduleNote')}
          </p>
        </Card>
      )}
    </div>
  )
}

function IntegrationCard({
  provider,
  name,
  description,
  icon,
  color,
  onClick,
}: IntegrationCardProps & { onClick: () => void }) {
  const { status, loading, refetch } = useIntegrationStatus(provider)
  const { connect, isConnecting } = useIntegrationConnect(provider, refetch)

  const handleConnect = (e: MouseEvent) => {
    e.stopPropagation()
    connect()
  }

  return (
    <div
      onClick={onClick}
      className="bg-white rounded-lg shadow-sm border p-6 cursor-pointer hover:shadow-md transition"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div
            className="w-12 h-12 rounded-lg flex items-center justify-center text-2xl"
            style={{ backgroundColor: `${color}15` }}
          >
            {icon}
          </div>
          <div>
            <h3 className="font-semibold text-gray-800">{name}</h3>
            <p className="text-sm text-gray-500 mt-1">{description}</p>
          </div>
        </div>
        {loading ? (
          <div className="w-20 h-6 bg-gray-100 rounded animate-pulse" />
        ) : status?.connected ? (
          <Badge variant="success">Yhdistetty</Badge>
        ) : (
          <Badge variant="default">Ei yhdistetty</Badge>
        )}
      </div>
      <div className="mt-4 flex justify-between items-center">
        {status !== null && !status.connected ? (
          <div className="flex items-center gap-2">
            <Button
              onClick={handleConnect}
              isLoading={isConnecting}
              style={{ backgroundColor: color }}
              className="text-white text-sm"
            >
              Yhdistä {name}
            </Button>
          </div>
        ) : (
          <span />
        )}
        <span
          className="text-sm font-medium"
          style={{ color }}
        >
          Hallitse →
        </span>
      </div>
    </div>
  )
}

// Import needed for the card component
import { useIntegrationStatus, useIntegrationConnect } from '../hooks'

export default function IntegrationsPage() {
  const { integrations, loading, error } = useIntegrationsList()
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null)

  if (selectedProvider) {
    return (
      <IntegrationDetail
        provider={selectedProvider}
        onBack={() => setSelectedProvider(null)}
      />
    )
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-800">Pankki-integraatiot</h1>
        <p className="text-gray-600 mt-2">
          Hallitse pankki- ja maksupääteyhteyksiäsi. Yhdistä tilejä tuodaksesi tapahtumia automaattisesti.
        </p>
      </div>

      {error && (
        <div className="mb-6">
          <ErrorMessage message={error} />
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-lg shadow-sm border p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gray-100 rounded-lg animate-pulse" />
                <div className="flex-1">
                  <div className="h-5 bg-gray-100 rounded w-24 animate-pulse" />
                  <div className="h-4 bg-gray-100 rounded w-40 mt-2 animate-pulse" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {integrations.map((integration) => {
            const config = PROVIDER_CONFIG[integration.id]
            if (!config) return null

            return (
              <IntegrationCard
                key={integration.id}
                provider={integration.id}
                name={config.name}
                description={config.description}
                icon={config.icon}
                color={config.color}
                onClick={() => setSelectedProvider(integration.id)}
              />
            )
          })}
        </div>
      )}

      <div className="mt-8 p-6 bg-blue-50 rounded-lg border border-blue-100">
        <h3 className="font-medium text-blue-900">Turvallisuustiedotus</h3>
        <p className="text-sm text-blue-700 mt-2">
          Pankkiyhteydet käyttävät Enable Banking -palvelua PSD2-rajapintojen kautta.
          Kirstunvartija ei tallenna pankkitunnuksiasi. Yhteydet ovat voimassa enintään 180 päivää,
          minkä jälkeen ne tulee uusia.
        </p>
      </div>
    </div>
  )
}
